import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged, User } from 'firebase/auth';
import { 
  Home, 
  Calendar, 
  Palette, 
  Search, 
  Users, 
  Sparkles,
  Bot,
  Plus,
  MessageCircle,
  Clock,
  Heart,
  BookOpen,
  Folder,
  Camera,
  MapPin,
  Star
} from 'lucide-react';
import { useChatbot } from './chat/GlobalChatbot.tsx';

interface NavItem {
  id: string;
  label: string;
  icon: React.ComponentType<any>;
  path: string;
  isActive?: boolean;
}

interface SidebarSection {
  title: string;
  items: NavItem[];
}

const LeftSidebar: React.FC = () => {
  const [isHovered, setIsHovered] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { isOpen: isChatOpen, toggleChat } = useChatbot();

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);



  // Mock data for chat history and recent projects
  const recentChats = [
    { id: '1', title: 'Wedding Budget Planning', time: '2 hours ago' },
    { id: '2', title: 'Venue Selection Help', time: 'Yesterday' },
    { id: '3', title: 'Decor Ideas Discussion', time: '3 days ago' },
  ];

  const recentProjects = [
    { id: '1', title: 'Sarah & Mike Wedding', type: 'plan', icon: Heart },
    { id: '2', title: 'Garden Party Theme', type: 'design', icon: Camera },
    { id: '3', title: 'Beach Wedding Venues', type: 'venue', icon: MapPin },
  ];

  const mainMenuItems: NavItem[] = [
    {
      id: 'home',
      label: 'Home',
      icon: Home,
      path: '/',
      isActive: location.pathname === '/'
    },
    {
      id: 'plan',
      label: 'Plan',
      icon: Calendar,
      path: '/plan',
      isActive: location.pathname.startsWith('/plan')
    },
    {
      id: 'design',
      label: 'Design',
      icon: Palette,
      path: '/design',
      isActive: location.pathname.startsWith('/design')
    },
    {
      id: 'explore',
      label: 'Explore',
      icon: Search,
      path: '/explore',
      isActive: location.pathname.startsWith('/explore')
    },
    {
      id: 'connect',
      label: 'Connect',
      icon: Users,
      path: '/connect',
      isActive: location.pathname.startsWith('/connect')
    }
  ];

  const settingsItems: NavItem[] = [];

  const userItems: NavItem[] = [];

  const sections: SidebarSection[] = [
    { title: '', items: mainMenuItems }
  ];

  const renderNavItem = (item: NavItem) => {
    const IconComponent = item.icon;
    
    return (
      <Link
        key={item.id}
        to={item.path}
        className={`group flex items-center rounded-xl transition-all duration-300 relative backdrop-blur-sm ${
          item.isActive
            ? 'bg-gradient-to-r from-[#FF4E8E]/20 to-[#FF4E8E]/10 text-[#FF4E8E] shadow-lg border border-[#FF4E8E]/20'
            : 'text-gray-700 hover:bg-white/10 hover:text-[#FF4E8E] hover:shadow-md hover:border hover:border-white/20'
        } ${isHovered ? 'px-3 py-2.5 justify-start' : 'px-2.5 py-2.5 justify-center'}`}
        style={{
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)'
        }}
      >
        <IconComponent 
          size={20} 
          strokeWidth={2}
          className={`flex-shrink-0 transition-colors ${
            item.isActive ? 'text-[#FF4E8E]' : ''
          }`}
        />
        <AnimatePresence mode="wait">
          {isHovered && (
            <motion.span
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              transition={{ duration: 0.15 }}
              className="ml-3 text-sm font-medium whitespace-nowrap"
            >
              {item.label}
            </motion.span>
          )}
        </AnimatePresence>
        {item.isActive && (
          <motion.div
            layoutId="activeIndicator"
            className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-[#FF4E8E] to-[#FF3A7D] rounded-r-full shadow-lg"
            style={{
              filter: 'drop-shadow(0 0 8px rgba(255, 78, 142, 0.5))'
            }}
            transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
          />
        )}
      </Link>
    );
  };

  const renderSection = (section: SidebarSection) => (
    <div key={section.title} className="space-y-1">
      <AnimatePresence mode="wait">
        {isHovered && section.title && (
          <motion.h3
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            transition={{ duration: 0.15 }}
            className="px-3 text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2"
          >
            {section.title}
          </motion.h3>
        )}
      </AnimatePresence>
      <div className="space-y-1">
        {section.items.map(renderNavItem)}
      </div>
    </div>
  );

  const renderNewChatButton = () => (
    <div className="p-2">
      <button
        onClick={toggleChat}
        className={`group flex items-center rounded-xl transition-all duration-300 w-full text-white shadow-xl border border-[#FF4E8E]/30 ${
          isHovered ? 'px-3 py-2.5 justify-start' : 'px-2.5 py-2.5 justify-center'
        }`}
        style={{
          background: 'linear-gradient(135deg, rgba(255, 78, 142, 0.8) 0%, rgba(255, 58, 125, 0.9) 100%)',
          backdropFilter: 'blur(15px)',
          WebkitBackdropFilter: 'blur(15px)',
          boxShadow: '0 8px 32px rgba(255, 78, 142, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 78, 142, 0.9) 0%, rgba(255, 58, 125, 1) 100%)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 78, 142, 0.8) 0%, rgba(255, 58, 125, 0.9) 100%)';
        }}
      >
        <Plus size={20} strokeWidth={2} className="flex-shrink-0" />
        <AnimatePresence mode="wait">
          {isHovered && (
            <motion.span
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              transition={{ duration: 0.15 }}
              className="ml-3 text-sm font-medium whitespace-nowrap"
            >
              New Chat
            </motion.span>
          )}
        </AnimatePresence>
      </button>
    </div>
  );

  const renderChatHistory = () => (
    <div className="space-y-1">
      <AnimatePresence mode="wait">
        {isHovered && (
          <motion.h3
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            transition={{ duration: 0.15 }}
            className="px-3 text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2"
          >
            CHAT HISTORY
          </motion.h3>
        )}
      </AnimatePresence>
      <div className="space-y-1">
        {isHovered ? (
          // Show all chats when hovered
          recentChats.map((chat) => (
            <div
              key={chat.id}
              className="group flex items-center rounded-xl transition-all duration-300 cursor-pointer text-gray-700 hover:bg-white/10 hover:text-[#FF4E8E] hover:shadow-md hover:border hover:border-white/20 px-3 py-2 justify-start backdrop-blur-sm"
              style={{
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)'
              }}
            >
              <MessageCircle size={18} strokeWidth={2} className="flex-shrink-0" />
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                transition={{ duration: 0.15 }}
                className="ml-3 flex-1 min-w-0"
              >
                <p className="text-sm font-medium truncate">{chat.title}</p>
                <p className="text-xs text-gray-500">{chat.time}</p>
              </motion.div>
            </div>
          ))
        ) : (
          // Show single chat icon when collapsed
          <div 
            className="group flex items-center rounded-xl transition-all duration-300 cursor-pointer text-gray-700 hover:bg-white/10 hover:text-[#FF4E8E] hover:shadow-md hover:border hover:border-white/20 px-2.5 py-2 justify-center backdrop-blur-sm"
            style={{
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)'
            }}
          >
            <MessageCircle size={18} strokeWidth={2} className="flex-shrink-0" />
          </div>
        )}
      </div>
    </div>
  );

  const renderRecentProjects = () => (
    <div className="space-y-1">
      <AnimatePresence mode="wait">
        {isHovered && (
          <motion.h3
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            transition={{ duration: 0.15 }}
            className="px-3 text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2"
          >
            RECENT PROJECTS
          </motion.h3>
        )}
      </AnimatePresence>
      <div className="space-y-1">
        {isHovered ? (
          // Show all projects when hovered
          recentProjects.map((project) => {
            const IconComponent = project.icon;
            return (
              <div
                key={project.id}
                className="group flex items-center rounded-xl transition-all duration-300 cursor-pointer text-gray-700 hover:bg-white/10 hover:text-[#FF4E8E] hover:shadow-md hover:border hover:border-white/20 px-3 py-2 justify-start backdrop-blur-sm"
                style={{
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)'
                }}
              >
                <IconComponent size={18} strokeWidth={2} className="flex-shrink-0" />
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.15 }}
                  className="ml-3 flex-1 min-w-0"
                >
                  <p className="text-sm font-medium truncate">{project.title}</p>
                  <p className="text-xs text-gray-500 capitalize">{project.type}</p>
                </motion.div>
              </div>
            );
          })
        ) : (
          // Show single project icon when collapsed
          <div 
            className="group flex items-center rounded-xl transition-all duration-300 cursor-pointer text-gray-700 hover:bg-white/10 hover:text-[#FF4E8E] hover:shadow-md hover:border hover:border-white/20 px-2.5 py-2 justify-center backdrop-blur-sm"
            style={{
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)'
            }}
          >
            <Heart size={18} strokeWidth={2} className="flex-shrink-0" />
          </div>
        )}
      </div>
    </div>
  );

  return (
    <motion.div
      className="fixed left-0 top-[72px] z-[10001] flex flex-col bg-white shadow-xl border-r border-gray-200"
      style={{ 
        height: 'calc(100vh - 72px)'
      }}
      initial={{ width: 64 }}
      animate={{ width: isHovered ? 320 : 64 }}
      transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >




      {/* Navigation Sections */}
      <div className="flex-1 overflow-y-auto py-4 space-y-6">
        <div className={`space-y-6 ${isHovered ? 'px-2' : 'px-1'}`}>
          {/* Main Navigation */}
          {sections.map(renderSection)}
          
          {/* New Chat Button */}
          {renderNewChatButton()}
          
          {/* Chat History */}
          {renderChatHistory()}
          
          {/* Recent Projects */}
          {renderRecentProjects()}
        </div>
      </div>




    </motion.div>
  );
};

export default LeftSidebar; 